import * as React from 'react';
import { Link } from 'react-router-dom';

import type { ShowcaseCardData } from '../../../content';
import { PersonByline } from '../../sections/people/PersonByline';
import './showcase-card.scss';

const Tag = (tag: string) => {
  return <span className="badge fs-xs text-nav bg-secondary text-decoration-none entry-tag">{tag}</span>;
};

const ShowcaseImage = ({ src, alt, className }: { src: string; alt: string; className: string }) => (
  <img src={src} alt={alt} className={className} loading="lazy" decoding="async" />
);

const isExternalLink = (href: string) => href.startsWith('http://') || href.startsWith('https://');

const ArticleLink: React.FC<{ href: string; className?: string; children: React.ReactNode }> = ({
  href,
  className,
  children,
}) =>
  isExternalLink(href) ? (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  ) : (
    <Link to={href} className={className}>
      {children}
    </Link>
  );

const FeaturedShowcaseCard = (showcase: ShowcaseCardData) => {
  const { articleLink, image, title, tags, date, description, author } = showcase;

  return (
    <article className="card h-100 border-0 shadow-sm">
      <div className="position-relative">
        <ArticleLink
          href={articleLink}
          className="btn btn-icon btn-light bg-white border-white btn-sm rounded-circle position-absolute top-0 end-0 zindex-5 me-3 mt-3"
        >
          <i className="bx bx-bookmark"></i>
        </ArticleLink>
        <ShowcaseImage src={image} alt={title} className="showcase-card-img-top" />
      </div>
      <div className="card-body pb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <span className="badge text-nav bg-secondary text-decoration-none entry-tag">
            {tags.map((tag, index) => (
              <React.Fragment key={tag}>
                {Tag(tag)}
                {index < tags.length - 1 && ' '}
              </React.Fragment>
            ))}
          </span>
          <span className="fs-sm text-muted entry-date">{date}</span>
        </div>
        <h3 className="h5 mb-3">
          <Link to={articleLink}>{title}</Link>
        </h3>
        <p className="fs-sm mb-3 text-dark">{description}</p>
      </div>
      <div className="card-footer py-4">
        <PersonByline name={author.name} role={author.role} image={author.image} />
      </div>
    </article>
  );
};

const CompactShowcaseCard = (showcase: ShowcaseCardData) => {
  const { articleLink, image, title, tags, date, description, author } = showcase;

  return (
    <article className="card border-0 shadow-sm overflow-hidden">
      <div className="row g-0">
        <div className="col-sm-5 position-relative showcase-card-img-side">
          <ShowcaseImage src={image} alt={title} className="showcase-card-img-side-image" />
          <ArticleLink
            href={articleLink}
            className="btn btn-icon btn-light bg-white border-white btn-sm rounded-circle position-absolute top-0 end-0 zindex-5 me-3 mt-3"
          >
            <i className="bx bx-bookmark"></i>
          </ArticleLink>
        </div>
        <div className="col-sm-7">
          <div className="card-body">
            <div className="d-flex align-items-center mb-3">
              <span className="badge text-nav bg-secondary text-decoration-none entry-tag">
                {tags.map((tag, index) => (
                  <React.Fragment key={tag}>
                    {Tag(tag)}
                    {index < tags.length - 1 && ','}
                  </React.Fragment>
                ))}
              </span>
              <span className="fs-sm text-muted border-start ps-3 ms-3 entry-date">{date}</span>
            </div>
            <h3 className="h5">
              <Link to={articleLink}>{title}</Link>
            </h3>
            <p className="fs-sm text-dark">{description}</p>
            <hr className="my-4" />
            <div className="d-flex flex-sm-nowrap flex-wrap align-items-center justify-content-between">
              <PersonByline name={author.name} role={author.role} image={author.image} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export const ShowcaseCard: React.FC<ShowcaseCardData> = (props) => {
  const { layout } = props;
  return layout === 'featured' ? <FeaturedShowcaseCard {...props} /> : <CompactShowcaseCard {...props} />;
};
